import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, FormControl, MenuItem, Select, Snackbar, Button, Typography, LinearProgress } from '@mui/material';
import apiService from '../../services/api';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import DeleteTeam from '../../components/Settings/DeleteTeam';
import Cookies from 'js-cookie';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const downloadIcon = apiService.getBlobAssetUrl('download.svg');
const search = apiService.getBlobAssetUrl('search.svg');
const suppliers = apiService.getBlobAssetUrl('suppliers.svg');
const deleteteams = apiService.getBlobAssetUrl('DeleteTeams_12.svg');
const closeimage = apiService.getBlobAssetUrl('close_12.svg');

const application_url = window._env_?.REACT_APP_APPLICATION_URL;

const TeamCard = ({ onSave }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [settings, setSettings] = useState([]);
  const [inviteRole, setInviteRole] = useState('');
  const [searchbarTerm, setSearchbarTerm] = useState('');
  const [members, setMembers] = useState([]);
  const [role, setRole] = useState([]);
  const [settingToDelete, setSettingToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [email, setEmail] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig] = useState({ key: null, direction: 'asc' });

  const accessToken = Cookies.get('access_token');

  const loadRoleAssignmentData = async () => {
    try {
      const data = await apiService.fetchData('role_assignment', accessToken);
      const results = data?.results || [];
      const formatted = results.map((item) => ({
        role: item.Role || '',
        iD: item.ID || '',
        emailID: item.EmailID || '',
        name: item.Name || '',
        createdBy: item.CreatedBy || '',
        createdOn: item.CreatedOn || '',
      }));
      setMembers(formatted);
    } catch (error) {
      console.error('Error fetching RoleAssignment:', error);
      setSnackbarMessage('Failed to load role assignments');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    loadRoleAssignmentData();
  }, []);

  const loadRoles = async () => {
    try {
      const data = await apiService.fetchData('roleslist', accessToken);
      const results = data?.results || [];
      const formatted = results.map((item) => ({
        role: item.Role || '',
        iD: item.ID || '',
      }));
      setRole(formatted);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setSnackbarMessage('Failed to load roles');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const data = await apiService.fetchData('userslist');
        const results = data?.results || [];
        const formatted = results.map((item) => ({
          ID: item.ID || '',
          Name: item.Name || '',
          EmailID: item.EmailID || '',
        }));
        setUsers(formatted);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    loadUserInfo();
  }, []);

  const loadTeams = async () => {
  setLoading(true);
  try {
    const data = await apiService.fetchData('teams', accessToken);
    const results = data?.results || [];

    const teamsWithMembers = await Promise.all(
      results.map(async (item) => {
        const teamId = item.ID || '';
        let members = [];

        try {
          const teamData = await apiService.fetchData(`teams/${teamId}`, accessToken);
          members = (teamData?.results || []).map((m) => ({
            name: m.Name || '',
            email: m.EmailID || '',
            role: m.Role || '',
          }));
        } catch (err) {
          console.error(`Error loading members for team ${teamId}:`, err);
        }

        return {
          id: teamId,
          name: item.Name || '',
          users: item.Users || 0,
          members, // 👈 key to enable search on member info
        };
      })
    );

    setTeams(teamsWithMembers);
  } catch (error) {
    console.error('Error fetching teams:', error);
    setSnackbarMessage('Failed to load teams');
    setSnackbarOpen(true);
  } finally {
    setLoading(false);
  }
};

  const loadTeamMembers = async (teamId) => {
    setLoading(true);
    try {
      const data = await apiService.fetchData(`teams/${teamId}`, accessToken);
      const results = data?.results || [];
      const formatted = results.map((item) => ({
        email: item.EmailID || '',
        name: item.Name || '',
        role: item.Role || '',
        iD: item.ID || item.EmailID || '',
      }));
      setMembers(formatted);
      setSettings(formatted);
    } catch (error) {
      console.error(`Error fetching members for team ${teamId}:`, error);
      setSnackbarMessage('Failed to load team members');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    let filtered = (members || []).filter(
      (member) =>
        (member?.name?.toLowerCase() ?? '').includes(searchbarTerm.toLowerCase()) ||
        (member?.email?.toLowerCase() ?? '').includes(searchbarTerm.toLowerCase()) ||
        (member?.role?.toLowerCase() ?? '').includes(searchbarTerm.toLowerCase())
    );

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortConfig.key] ?? '';
        const bVal = b[sortConfig.key] ?? '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [members, searchbarTerm, sortConfig]);

  const totalRows = filteredUsers.length;
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredUsers.slice(indexOfFirstRow, indexOfLastRow);

  const maxVisiblePages = 3;

  const getPageRange = () => {
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return { startPage, endPage, totalPages };
  };

  const { startPage, endPage, totalPages } = getPageRange();

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleSuggestionClick = (user) => {
    setEmail(user.EmailID);
    setSuggestions([]);
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([loadRoles(), loadTeams()]);
    };
    fetchData();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when search term changes
  }, [searchbarTerm]);

  const handleTeamSelect = async (teamId, teamName) => {
    setSelectedTeam(teamName);
    setSelectedTeamId(teamId);
    setCurrentPage(1); // Reset to page 1 when selecting a new team
    await loadTeamMembers(teamId);
  };

  const handleEmailInputChange = (e) => {
    const input = e.target.value;
    setEmail(input);

    if (input.trim() === '') {
      setSuggestions([]);
      return;
    }

    const filteredSuggestions = users.filter(
      (user) =>
        user?.EmailID?.toLowerCase().includes(input.toLowerCase()) ||
        user?.Name?.toLowerCase().includes(input.toLowerCase())
    );

    setSuggestions(filteredSuggestions);
  };

  const handleSendInvite = async () => {
    const trimmedEmail = email.trim().toLowerCase(); // Normalize email to lowercase
    const nameFromEmail = trimmedEmail.split('@')[0];

    // Validate inputs
    if (!trimmedEmail || !inviteRole || !selectedTeamId) {
      setSnackbarMessage('Please enter an email, select a role, and choose a team.');
      setSnackbarOpen(true);
      return;
    }

    const userId = sessionStorage.getItem('user_id');
    const roleId = typeof inviteRole === 'object' ? inviteRole.iD : Number(inviteRole);
    const teamId = selectedTeamId;
    const createdBy = userId;

    // Validate roleId
    if (typeof roleId !== 'number' || isNaN(roleId)) {
      setSnackbarMessage('Invalid role selected. Please select a valid role.');
      setSnackbarOpen(true);
      return;
    }

    try {
  // Step 1: Fetch team members to check if user is already in the team
  const teamMembers = await apiService.fetchData(`teams/${teamId}`, accessToken);
  const isUserInTeam = teamMembers?.results?.some((member) => member.EmailID?.toLowerCase() === trimmedEmail);
 
  if (isUserInTeam) {
    setSnackbarMessage('This user is already a member of the team.');
    setSnackbarOpen(true);
    return; // Stop further processing for existing team member
  }
 
  // Step 2: Check if user exists in the system
  const userData = await apiService.fetchData('userslist', accessToken);
  const existingUser = userData?.results?.find((user) => user.EmailID?.toLowerCase() === trimmedEmail);
  let newUserId = existingUser?.ID;
 
  // Step 3: Validate required fields
  if (!newUserId || !roleId || !teamId) {
    throw new Error('UserID, RoleID, and PracticeID are required');
  }
 
  // Step 4: Attempt to assign role to user using teams/${teamId}
  const inviteData = {
    UserID: newUserId,
    RoleID: roleId,
    PracticeID: teamId,
    CreatedBy: createdBy,
  };
 
  const response = await apiService.postData(`teams/${teamId}`, inviteData, accessToken);
  console.log('Backend Invite Success:', response);
 
  // Step 5: Handle backend response for already assigned or invited users
  if (response?.message?.includes('already exists')) {
    setSnackbarMessage('This user already has a role assigned.');
    setSnackbarOpen(true);
    return; // Stop further processing
  }
  if (response?.message?.includes('already been invited')) {
    setSnackbarMessage('This user has already been invited.');
    setSnackbarOpen(true);
    return; // Stop further processing
  }
  if (response?.message?.includes('already a member')) {
    setSnackbarMessage('This user is already a member of the team.');
    setSnackbarOpen(true);
    return; // Stop further processing
  }
 
  // Step 6: Send email invitation only after successful role assignment
  const emailPayload = {
    endpoint: 'send_email',
    customerName: nameFromEmail,
    surveyLink: '',
    surveyType: '',
    projectName: '',
    send_to: trimmedEmail,
    subject: 'Invitation to CSAT',
    token: accessToken,
    templateType: 'TeamInvitation',
    RoleName: inviteRole.role || 'Member', // Fallback role name
    appLink: application_url,
    teamName: selectedTeam,
  };
 
  const emailResponse = await apiService.sendEmail(
    emailPayload.endpoint,
    emailPayload.customerName,
    emailPayload.surveyLink,
    emailPayload.surveyType,
    emailPayload.projectName,
    emailPayload.send_to,
    emailPayload.subject,
    emailPayload.token,
    emailPayload.templateType,
    emailPayload.RoleName,
    emailPayload.appLink,
    emailPayload.teamName
  );
 
  // Step 7: Verify user ID from email response if needed
  if (emailResponse?.userId && !newUserId) {
    newUserId = emailResponse.userId;
  }
 
  // Step 8: Refresh team members
  await loadTeamMembers(teamId);
 
  // Step 9: Reset form and show success
  setSnackbarMessage('Invitation sent successfully!');
  setSnackbarOpen(true);
  setEmail('');
  setInviteRole('');
  setSearchbarTerm('');
} catch (error) {
  console.error('Invite Error:', error.response?.data || error);
  const message = error.response?.data?.message || error.message || 'Unknown error occurred.';
  setSnackbarMessage(`Invite failed: ${message}`);
  setSnackbarOpen(true);
}

  };

  const handleRoleChange = useCallback(
    async (userId, selectedRole) => {
      if (!userId || !selectedRole) {
        setSnackbarMessage('Missing user ID or role.');
        setSnackbarOpen(true);
        return;
      }

      const teamId = selectedTeamId || 1;
      if (!teamId || teamId <= 0) {
        setSnackbarMessage('Invalid team ID.');
        setSnackbarOpen(true);
        return;
      }
      const user_Id = sessionStorage.getItem('user_id');
      const payload = {
        ID: userId,
        RoleID: selectedRole,
        PracticeID: teamId,
        ModifiedBy: user_Id,
      };

      try {
        const response = await apiService.putData(`teams/${teamId}`, payload, accessToken);
        console.log('Role update success:', response);
        setSnackbarMessage('Permissions successfully updated!');
        setSnackbarOpen(true);

        if (typeof onSave === 'function') {
          onSave({ iD: userId, role: selectedRole });
        }

        await loadTeamMembers(teamId);
      } catch (error) {
        console.error('Failed to update permissions:', error);
        console.error('Server response:', error.response?.data);
        setSnackbarMessage(`Failed to update permissions: ${error.response?.data?.message || error.message}`);
        setSnackbarOpen(true);
      }
    },
    [accessToken, onSave, selectedTeamId]
  );

  const handleClearInput = () => {
    setEmail('');
    setSuggestions([]);
  };

  const handleConfirmDelete = async () => {
    const teamId = selectedTeamId;
    try {
      await apiService.deleteData(`teams/${teamId}`, { ID: settingToDelete.iD }, accessToken);
      setSnackbarMessage('User removed from the team successfully.');
      setSnackbarOpen(true);
      setShowDeleteModal(false);
      await loadTeamMembers(teamId);
    } catch (error) {
      console.error('Failed to remove user from team:', error);
      setSnackbarMessage(`Failed to remove user: ${error.response?.data?.message || error.message}`);
      setSnackbarOpen(true);
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSettingToDelete(null);
  };

  const handleDeleteClick = (row) => {
    setSettingToDelete(row);
    setShowDeleteModal(true);
  };

  const toggleDownloadMenu = () => setShowDownloadMenu(!showDownloadMenu);

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text('Teams Table', 10, 10);
    autoTable(doc, {
      startY: 20,
      head: [['ID', 'EmailID', 'name', 'Role']],
      body: settings.map((row) => [
        row.iD,
        row.email,
        row.name,
        row.role,
      ]),
    });
    doc.save('teams.pdf');
  };

  const exportToCSV = () => {
    const headers = ['ID', 'EmailID', 'name', 'Role'].join(',');
    const rows = settings.map((row) =>
      [
        row.iD,
        row.email,
        row.name,
        row.role,
      ]
        .map((field) => `"${field}"`)
        .join(',')
    );
    const csvContent = `data:text/csv;charset=utf-8,${headers}\n${rows.join('\n')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'teams_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderSuggestionText = (text, searchTerm) => {
    if (!searchTerm) return <span>{text}</span>;

    const lowerText = text?.toLowerCase() ?? '';
    const lowerSearchTerm = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearchTerm);

    if (index === -1) return <span>{text}</span>;

    const beforeMatch = text.substring(0, index);
    const match = text.substring(index, index + searchTerm.length);
    const afterMatch = text.substring(index + searchTerm.length);

    return (
      <>
        <span className="suggestion-non-match">{beforeMatch}</span>
        <span className="suggestion-match">{match}</span>
        <span className="suggestion-non-match">{afterMatch}</span>
      </>
    );
  };

  if (loading) {
    return (
      <Box id="loading1">
        <Box id="loading21" sx={{ mb: 2 }}>
          <LinearProgress id="loading3" />
        </Box>
        <Typography id="loading4" variant="h6">
          Loading Teams Data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className="team-container">
      <div className="app-container" id="app-container1">
        {!selectedTeam ? (
          <div>
            <div className="team-name-bar" id="team-name-bar11">
            <h2 className="title" id="title1" title="Teams">Teams</h2>
            <div className="search-container1">
                <input
                  title="Search Team and User"
                  type="search"
                  placeholder="Search Team and User"
                  value={searchbarTerm}
                  onChange={(e) => setSearchbarTerm(e.target.value)}
                  className="search-input2"
                />
                <img src={search} alt="Search" className="search-img1" />
              </div>
              </div>
            <div className="teams-grid">
              {teams
  .filter((team) => {
    const term = searchbarTerm.toLowerCase();

    const matchesBasic =
      team.name.toLowerCase().includes(term) ||
      team.id.toString().includes(term) ||
      team.users.toString().includes(term);

    const matchesMember = (team.members || []).some((member) =>
      (member.name?.toLowerCase() || '').includes(term) ||
      (member.email?.toLowerCase() || '').includes(term) ||
      (member.role?.toLowerCase() || '').includes(term)
    );

    return matchesBasic || matchesMember;
  })

  .map((team) => (

                <button
                  key={team.id}
                  className="team-card"
                  onClick={() => handleTeamSelect(team.id, team.name)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleTeamSelect(team.id, team.name);
                    }
                  }}
                  type="button"
                  tabIndex={0}
                >
                  <h3 className="team1" id={`team-name-${team.id}`} title={team.name}>{team.name}</h3>
                  <div className="team3" id={`team-count-${team.id}`}>
                    <img src={suppliers} alt="Supplier Icon" className="logo-img" />
                    <p className="team2" id={`team-count-text-${team.id}`} title={team.users}>{team.users}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="team-details-container">
            <h2>
              <button
                title="Teams"
                onClick={() => {
                  setSelectedTeam(null);
                  setSelectedTeamId(null);
                  setMembers([]);
                  setCurrentPage(1);
                }}
                id="teams1"
                aria-label="Go back to Teams list"
              >
                Teams
              </button>{" "}
              / <span id="teams2" title="Team Details">Team Details</span>
            </h2>
            <p id="teams3" title="Send Invite to add them in Org and Teams">Send Invite to add them in Org and Teams</p>
            <div className="invite-container1">
              <div className="email-input-wrapper">
                <input
                  title="Type CriticalRiver email ID"
                  type="email"
                  placeholder="Type CriticalRiver email ID"
                  value={email}
                  onChange={handleEmailInputChange}
                  className="email-input1"
                />
                {email && (
                  <button
                    type="button"
                    className="clear-img11"
                    onClick={handleClearInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleClearInput();
                      }
                    }}
                    aria-label="Clear email input"
                    tabIndex={0}
                  >
                    <img src={closeimage} alt="clear" />
                  </button>
                )}
                {suggestions.length > 0 && (
                  <ul className="suggestions-dropdown">
                    {suggestions.map((user, index) => (
                      <li
                        key={user.ID}
                        onClick={() => handleSuggestionClick(user)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleSuggestionClick(user);
                          }
                        }}
                        className={`suggestion-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                        tabIndex={0}
                      >
                        <div>{renderSuggestionText(user.Name, email)}</div>
                        <div>{renderSuggestionText(user.EmailID, email)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <FormControl className="role-select1">
                <Select
  title="Select Role"
  value={inviteRole ? JSON.stringify(inviteRole) : ''}
  onChange={(e) => {
    const value = e.target.value;
    if (!value) {
      setInviteRole(''); // Or set to null or {} depending on your logic
      return;
    }
    try {
      const selected = JSON.parse(value);
      setInviteRole(selected);
    } catch (error) {
      console.error('Failed to parse role:', error, 'Value:', value);
      setSnackbarMessage('Invalid role selection');
      setSnackbarOpen(true);
    }
  }}
  displayEmpty
  placeholder="Select Role"
>
<MenuItem value="">Select Role</MenuItem>
  {role.map((r) => (
<MenuItem key={r.iD} value={JSON.stringify(r)}>
      {r.role}
</MenuItem>
  ))}
</Select>
              </FormControl>
              <Button
                title="Send Invite"
                type="button"
                className="invite-button"
                id="invite-button11"
                onClick={handleSendInvite}
              >
                Send Invite
              </Button>
            </div>
            <h2 id="teamname" title="Team Name">Team Name</h2>
            <div className="team-name-bar" id="team-name-bar1">
              <input
                title={selectedTeam}
                type="text"
                value={selectedTeam}
                readOnly
                id="team-name-bar2"
              />
              <div className="search-container1">
                <input
                  title="Search User"
                  type="search"
                  placeholder="Search User"
                  value={searchbarTerm}
                  onChange={(e) => setSearchbarTerm(e.target.value)}
                  className="search-input2"
                />
                <img src={search} alt="Search" className="search-img1" />
              </div>
            </div>
            <div id="results4">
              <div className="settings-download-wrapper">
                <button
                  className="download-icon-button"
                  onClick={toggleDownloadMenu}
                  aria-label="Toggle download options"
                >
                  <img src={downloadIcon} alt="Download" className="settings-download-icon" />
                </button>
                {showDownloadMenu && (
                  <div className="Project-export-dropdown">
                <button id="Project-export-option11"
  className="Project-export-option"
  onClick={exportToCSV}
  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') exportToCSV(); }}
  aria-label="Download CSV file"
>
  Download CSV
</button>
<button id="Project-export-option11"
  className="Project-export-option"
  onClick={exportPDF}
  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') exportPDF(); }}
  aria-label="Download PDF file"
>
  Download PDF
</button>
              </div>
                )}
              </div>
              <Box>
                <Typography id="results3">
                  Results: {totalRows === 0 ? 0 : indexOfFirstRow + 1}-{Math.min(indexOfLastRow, totalRows)} of {totalRows}
                </Typography>
              </Box>
              <select
                className="items-per-page-select" id="items-per-page-select1"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  setCurrentPage(1); // Reset to page 1 when rows per page changes
                }}
              >
                {[5, 10, 15, 20].map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </div>
            <div className="member-list1">
              {currentRows.length === 0 ? (
                <Typography id="members1" title="No members found for the current search.">No members found for the current search.</Typography>
              ) : (
                currentRows.map((user) => (
                  <div key={user.iD || user.email} className="member-item1">
                    <div className="member-info1">
                      <p className="member-name1" title={user.name}>{user.name}</p>
                      <p className="member-email1" title={user.email}>{user.email}</p>
                    </div>
                    <FormControl className="member-role-select1">
                      <Select
                        title={user.role}
                        value={user.role}
                        onChange={(e) => {
                          const updatedRole = e.target.value;
                          handleRoleChange(user.iD, updatedRole);
                        }}
                        displayEmpty
                        renderValue={(selected) => {
                          const selectedRole = role.find((r) => r.role === selected);
                          return selectedRole ? selectedRole.role : user.role;
                        }}
                      >
                        <MenuItem value="">Select Role</MenuItem>
                        {role.map((r) => (
                          <MenuItem key={r.iD} value={r.iD}>
                            {r.role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <button
                      type="button"
                      id="delete-img21"
                      className="text-red-600 hover:text-red-800"
                      onClick={() => handleDeleteClick(user)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleDeleteClick(user);
                        }
                      }}
                      aria-label={`Remove ${user.name} from team`}
                      tabIndex={0}
                    >
                      <img src={deleteteams} alt="delete icon" className="delete-img1" title="delete" />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="Settings-table-footer">
              <div className="Settings-pagination">
                <button onClick={handlePrevious} disabled={currentPage === 1} className="Settings-pagination-button">
                  <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                  const pageNumber = startPage + i;
                  const isSelected = currentPage === pageNumber;
                  const buttonClass = `Settings-pagination-button ${isSelected ? 'Settings-selected' : ''}`;
                  return (
                    <button key={pageNumber} onClick={() => paginate(pageNumber)} className={buttonClass}>
                      {pageNumber}
                    </button>
                  );
                })}
                <button onClick={handleNext} disabled={currentPage === totalPages} className="Settings-pagination-button">
                  <FontAwesomeIcon icon={faAngleRight} />
                </button>
              </div>
            </div>
          </div>
        )}
        {showDeleteModal && (
          <div className="modal-overlay">
            <DeleteTeam
              settingName={settingToDelete.name}
              onConfirm={handleConfirmDelete}
              onCancel={handleCancelDelete}
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarMessage={setSnackbarMessage}
            />
          </div>
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          sx={{ '& .MuiSnackbarContent-root': { backgroundColor: snackbarMessage.includes('successful') ? '#4caf50' : '#f44336' } }}
        />
      </div>
    </div>
  );
};

TeamCard.propTypes = {
  onSave: PropTypes.func.isRequired,
};

export default TeamCard;